import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MessageService } from 'primeng/api'
import { Router, ActivatedRoute } from '@angular/router'
import { NgxSpinnerService } from 'ngx-spinner'
import { AuthService } from '../../services/auth.service'
import { CurrentDeviceService } from '@app/shared/services/current-device.service'
import { TranslateService } from '@ngx-translate/core'
import { Location } from '@angular/common'
import { ClientContextService } from '@app/core/services/client-context.service'
import { OrgFinderComponent } from '../org-finder/org-finder.component'
import { environment } from 'src/config/environments/environment'

@Component({ selector: 'app-login', templateUrl: './login.component.html', styleUrls: ['./login.component.scss'], standalone: false })
export class LoginComponent implements OnInit {
  get isRtl(): boolean {
    return this.translate.currentLang === 'he'
  }
  @ViewChild('orgFinderModal') orgFinderModal: OrgFinderComponent

  form: FormGroup
  title = 'AUTH.TITLE'

  /** Keyera Web link page — lets pass holders issue/manage guest passes without a C-Portal account */
  keyeraWebUrl = environment.keyeraWebUrl

  // Language selection
  languages = [
    { value: 'en', name: 'English' },
    { value: 'he', name: 'עברית' },
    { value: 'es', name: 'Español' },
  ]
  selectedLanguage: string = 'en'

  // Organization context
  clientId: string | null = null
  isOrgLogin: boolean = false
  clientHasSAML: boolean = false
  orgName: string = ''

  // Account lockout handling
  isAccountLocked: boolean = false

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private clientContextService: ClientContextService,
    private spinner: NgxSpinnerService,
    private messageService: MessageService,
    public currentDeviceService: CurrentDeviceService,
    private translate: TranslateService,
    private location: Location,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Initialize language from browser or localStorage
    const storedLang = localStorage.getItem('selectedLanguage')
    if (storedLang) {
      this.selectedLanguage = storedLang
      this.translate.use(storedLang)
    } else {
      const browserLang = navigator.language.split('-')[0]
      this.selectedLanguage = this.languages.find((l) => l.value === browserLang)?.value || 'en'
      this.translate.use(this.selectedLanguage)
    }
    this.updateDirection()

    // Check if this is an org login (from route data)
    this.route.data.subscribe((data) => {
      this.isOrgLogin = (data['hasClientContext'] as boolean | undefined) || false
    })

    // If org login, get clientId from route
    if (this.isOrgLogin) {
      this.route.parent?.params.subscribe((params) => {
        this.clientId = params['clientId'] as string
        if (this.clientId) {
          this.clientContextService.setClientId(this.clientId)
          this.checkIfClientHasSAML(this.clientId)
        }
      })
    }

    this.initForm()
    this.authService.countInvalidTimes = 0
    this.blockPreviousPage()
  }

  initForm() {
    this.form = this.fb.group({
      email: ['', [Validators.required]],
      password: ['', [Validators.required]],
    })
  }

  onSubmit(_e: Event) {
    if (this.form.valid) {
      this.spinner.show()
      const value = this.form.value as { email: string; password: string }

      // Pass clientId only if org user
      this.authService.login(value.email, value.password, this.clientId || undefined).subscribe(this.onLoginSuccess.bind(this), this.onLoginError.bind(this))
    } else {
      this.handleValidationError()
    }
  }

  async onLoginSuccess(_data: unknown) {
    await this.authService.getUser(true)
    this.spinner.hide()

    // Navigate to root - let the HomePageRedirectGuard or PermissionGuard determine the correct page
    if (this.isOrgLogin && this.clientId) {
      this.router.navigate([`/org/${this.clientId}`])
    } else {
      this.router.navigate(['/'])
    }
  }

  /**
   * When the email or the password are invalid show error message
   * @param data
   */
  onLoginError(data: { status?: number; error?: { message?: string; user_name?: string; remainingAttempts?: number } } | null | undefined) {
    this.spinner.hide()

    // Handle 429 - Account locked due to too many failed attempts
    if (data?.status === 429) {
      this.handleAccountLockout(data)
      return
    }

    // Handle two-factor authentication
    if (data?.error?.message == 'TWO_FACTOR_AUTH') {
      this.authService.form = this.form

      this.authService.verifyBody = {
        user_name: data?.error?.user_name,
        password: this.form.get('password')?.value as string,
      }
      this.router.navigate(['/auth/validate-pin', { isVerify: true }])
      return
    }

    // Handle remaining attempts warning (if backend provides it)
    if (data?.status === 401 && data?.error?.remainingAttempts !== undefined) {
      const remaining = data.error.remainingAttempts

      if (remaining === 1) {
        // Last attempt warning
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('AUTH.ERRORS.WARNING_LAST_ATTEMPT.TITLE'),
          detail: this.translate.instant('AUTH.ERRORS.WARNING_LAST_ATTEMPT.MESSAGE'),
          life: 5000,
        })
      } else if (remaining <= 3) {
        // Progressive warning for 2-3 attempts remaining
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('AUTH.ERRORS.WARNING_FEW_ATTEMPTS.TITLE'),
          detail: this.translate.instant('AUTH.ERRORS.WARNING_FEW_ATTEMPTS.MESSAGE', { count: remaining }),
          life: 5000,
        })
      } else {
        // Show remaining attempts
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('AUTH.ERRORS.REMAINING_ATTEMPTS.TITLE'),
          detail: this.translate.instant('AUTH.ERRORS.REMAINING_ATTEMPTS.MESSAGE', { count: remaining }),
        })
      }
    } else {
      // Default invalid credentials message
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('AUTH.ERRORS.INVALID_USERNAME_OR_PASSWORD.TITLE'),
        detail: this.translate.instant('AUTH.ERRORS.INVALID_USERNAME_OR_PASSWORD.MESSAGE'),
      })
    }
  }

  /**
   * Handle 429 account lockout response
   * @param data Error response from backend
   */
  private handleAccountLockout(_data: unknown) {
    this.isAccountLocked = true

    // Show lockout message
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('AUTH.ERRORS.ACCOUNT_LOCKED.TITLE'),
      detail: this.translate.instant('AUTH.ERRORS.ACCOUNT_LOCKED.MESSAGE'),
      life: 8000,
    })
  }

  /**
   * When the username or password are not filled show error message
   */
  handleValidationError() {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('AUTH.ERRORS.PLEASE_FILL_ALL_FIELDS.TITLE'),
      detail: this.translate.instant('AUTH.ERRORS.PLEASE_FILL_ALL_FIELDS.MESSAGE'),
    })
  }

  /**
   * Block go back to previous page
   */
  blockPreviousPage() {
    window.history.pushState(null, '', window.location.href)

    this.location.subscribe(() => {
      window.history.pushState(null, '', window.location.href)
    })
  }

  /**
   * Check if this client has SAML enabled (only for org users)
   */
  async checkIfClientHasSAML(clientId: string) {
    try {
      const response = await this.authService.checkClientSAMLStatus(clientId)
      this.clientHasSAML = response.saml_enabled
      this.orgName = response.name || ''

      // Auto-redirect to SAML if enabled
      if (this.clientHasSAML) {
        const origin = window.location.origin
        window.location.href = `${environment.serverUrl}/auth/saml/login?clientId=${this.clientId}&origin=${encodeURIComponent(origin)}`
      }
    } catch (_error) {
      this.clientHasSAML = false
    }
  }

  /**
   * SAML login - only for org users
   */
  loginWithSAML() {
    if (!this.clientId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('AUTH.ERRORS.NO_ORG_CONTEXT.TITLE'),
        detail: this.translate.instant('AUTH.ERRORS.NO_ORG_CONTEXT.MESSAGE'),
      })
      return
    }

    // Redirect to backend SAML login endpoint
    const origin = window.location.origin
    window.location.href = `${environment.serverUrl}/auth/saml/login?clientId=${this.clientId}&origin=${encodeURIComponent(origin)}`
  }

  /**
   * Open organization finder modal
   */
  openOrgFinder() {
    if (this.orgFinderModal) {
      this.orgFinderModal.open()
    }
  }

  onLanguageChange(event: { value: string }) {
    const lang = event.value
    this.translate.use(lang)
    localStorage.setItem('selectedLanguage', lang)
    this.updateDirection()
  }

  private updateDirection() {
    const dir = this.selectedLanguage === 'he' ? 'rtl' : 'ltr'
    document.dir = dir
  }
}
